import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ChangeEvent } from 'react';

import type { ThemeColorKey, ThemeColors } from '@/domain/models/theme.js';
import { THEME_COLOR_GROUPS } from '@/domain/models/theme.js';
import { useApp } from '@/presentation/hooks/use-app.js';

export const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

type ColorFieldProps = {
    colorKey: ThemeColorKey;
    value: string;
    onChange: (key: ThemeColorKey, value: string) => void;
    label: string;
};

export function ColorField({ colorKey, value, onChange, label }: ColorFieldProps) {
    const { translate } = useApp();
    const invalid = value.length > 0 && !HEX_REGEX.test(value);

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
                component="input"
                type="color"
                value={HEX_REGEX.test(value) ? value : '#000000'}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(colorKey, event.target.value.toUpperCase())}
                sx={{
                    width: 40,
                    height: 40,
                    p: 0.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    flexShrink: 0
                }}
            />
            <TextField
                label={label}
                value={value}
                onChange={(event) => {
                    const next = event.target.value.startsWith('#') ? event.target.value : `#${event.target.value}`;
                    onChange(colorKey, next);
                }}
                size="small"
                fullWidth
                error={invalid}
                helperText={invalid ? translate('clients.theme.invalidHex') : undefined}
                inputProps={{ maxLength: 7 }}
            />
        </Stack>
    );
}

type ThemePaletteEditorProps = {
    colors: ThemeColors;
    onColorsChange: (colors: ThemeColors) => void;
};

export function ThemePaletteEditor({ colors, onColorsChange }: ThemePaletteEditorProps) {
    const { translate } = useApp();

    const handleColorChange = (key: ThemeColorKey, value: string) => {
        onColorsChange({ ...colors, [key]: value });
    };

    return (
        <Stack spacing={2.5}>
            {THEME_COLOR_GROUPS.map((group) => (
                <Box key={group.titleKey}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                        {translate(group.titleKey)}
                    </Typography>
                    <Grid container spacing={2}>
                        {group.keys.map((key) => (
                            <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                                <ColorField colorKey={key} value={colors[key]} onChange={handleColorChange} label={translate(`clients.theme.colors.${key}`)} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Stack>
    );
}

export function areThemeColorsValid(colors: ThemeColors): boolean {
    return Object.values(colors).every((value) => HEX_REGEX.test(value));
}
